import React, { useEffect, useState } from 'react';
import { useOneButtonStore } from '../../../../store/oneButtonStore';
import { GuideBotAvatar3D } from './GuideBotAvatar3D';
import './oneButton-css/GuideBotOverlay.css';

/** Viewport-fixed bot (legacy dock mode). */
const BOT_SIZE_FIXED = 56;
/** In-rail dock: matches section rail icon buttons (~42px column). */
const BOT_SIZE_RAIL = 40;
/** Offset from the target section’s top-left corner. */
const VIEW_MARGIN = 8;
const BOT_LABEL_SPACE = 18;
const RAIL_HOME_GAP = 8;

function getSectionRailRect(): DOMRect | null {
    const rail = document.querySelector('.obd-dashboard-section-rail') as HTMLElement | null;
    if (!rail) return null;
    const r = rail.getBoundingClientRect();
    if (!r.width || !r.height) return null;
    return r;
}

function getVerticalBounds(): { minTop: number; maxTop: number } {
    if (typeof window === 'undefined') return { minTop: VIEW_MARGIN, maxTop: 600 };

    const headerEl = document.querySelector('.editor-dashboard-header') as HTMLElement | null;
    const liveGuideFooterEl = document.querySelector('.obd-dashboard-agent-footer') as HTMLElement | null;

    const headerBottom = headerEl?.getBoundingClientRect().bottom ?? VIEW_MARGIN;
    const footerTop = liveGuideFooterEl?.getBoundingClientRect().top ?? window.innerHeight;

    const minTop = Math.max(VIEW_MARGIN, Math.round(headerBottom + 8));
    const maxTop = Math.round(footerTop - BOT_SIZE_FIXED - BOT_LABEL_SPACE - VIEW_MARGIN);

    return {
        minTop,
        maxTop: Math.max(minTop, maxTop),
    };
}

function clampBotPosition(left: number, top: number): { left: number; top: number } {
    if (typeof window === 'undefined') return { left, top };
    const maxL = window.innerWidth - BOT_SIZE_FIXED - VIEW_MARGIN;
    const { minTop, maxTop } = getVerticalBounds();
    return {
        left: Math.min(Math.max(VIEW_MARGIN, left), maxL),
        top: Math.min(Math.max(minTop, top), maxTop),
    };
}

/** Always dock under the section rail, centered on its column (commerce guide no longer moves the bot). */
function homePosition(): { left: number; top: number } {
    const railRect = getSectionRailRect();
    if (railRect) {
        const left = Math.round(railRect.left + (railRect.width - BOT_SIZE_FIXED) / 2);
        const top = Math.round(railRect.bottom + RAIL_HOME_GAP);
        return clampBotPosition(left, top);
    }
    const { maxTop } = getVerticalBounds();
    return clampBotPosition(VIEW_MARGIN + 8, maxTop);
}

export interface GuideBotOverlayProps {
    /** Opens the Gemini opportunity assistant (Sunny). */
    onOpenAssistant?: () => void;
    /**
     * `rail`: last child of `.obd-dashboard-section-rail` so expand/collapse reflows position.
     * `fixed`: viewport-fixed coords under the rail (legacy; can overlap when rail scrolls or grows).
     * `bottom`: viewport-fixed, bottom-left above the live guide footer (outside transformed overlay).
     */
    dock?: 'rail' | 'fixed' | 'bottom';
}

/** Opportunity assistant entry: rail, fixed near rail, or bottom of viewport. */
export const GuideBotOverlay: React.FC<GuideBotOverlayProps> = ({ onOpenAssistant, dock = 'rail' }) => {
    const theme = useOneButtonStore((s) => s.theme);
    const productsOpen = useOneButtonStore((s) => s.commerceHubProductsOpen);
    const quotesOpen = useOneButtonStore((s) => s.commerceHubQuotesOpen);
    const agreementsOpen = useOneButtonStore((s) => s.commerceHubAgreementsOpen);

    const modalOpen = productsOpen || quotesOpen || agreementsOpen;

    const [pos, setPos] = useState(() => (dock === 'fixed' ? homePosition() : { left: 0, top: 0 }));

    useEffect(() => {
        if (dock !== 'fixed' || modalOpen) return;

        const syncHome = () => {
            setPos(homePosition());
        };

        syncHome();
        window.addEventListener('resize', syncHome, { passive: true });
        const overlay = document.querySelector('.editor-dashboard-overlay');
        overlay?.addEventListener('scroll', syncHome, { passive: true });

        return () => {
            window.removeEventListener('resize', syncHome);
            overlay?.removeEventListener('scroll', syncHome);
        };
    }, [dock, modalOpen]);

    const fixedStyle =
        dock === 'fixed'
            ? {
                  left: pos.left,
                  top: pos.top,
                  width: BOT_SIZE_FIXED,
                  height: BOT_SIZE_FIXED,
              }
            : undefined;

    const dockClass =
        dock === 'rail'
            ? 'obd-guide-bot--docked-rail'
            : dock === 'bottom'
              ? 'obd-guide-bot--docked-bottom'
              : 'obd-guide-bot--idle-rail';

    return (
        <div
            className={`obd-guide-bot obd-theme-${theme}${modalOpen ? ' obd-guide-bot--hidden' : ''} ${dockClass}`}
            style={fixedStyle ?? {}}
        >
            <button
                type="button"
                className="obd-guide-bot__hit"
                onClick={(e) => {
                    e.stopPropagation();
                    onOpenAssistant?.();
                }}
                aria-label="Open Sunny, the opportunity assistant chat"
                title="Ask Sunny about this opportunity"
            >
                <div className="obd-guide-bot__flip">
                    <div className="obd-guide-bot__inner obd-guide-bot__inner--3d">
                        <GuideBotAvatar3D walking={false} />
                    </div>
                </div>
            </button>
            <span className="obd-guide-bot__label">Sunny</span>
            <span className="obd-guide-bot__shadow" aria-hidden />
        </div>
    );
};

export default GuideBotOverlay;
